import { useState } from "react";
import { Box, Button, Checkbox, Dialog, makeStyles, Typography, Fab, Tooltip, Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import useKitApi, { KitForm } from "../../api/KitApi";
import useFormApi from "../../api/FormApi";
import useOrderApi, { Order } from "../../api/OrderApi";
import { NamedIdentifiable } from "../../model/Items";

interface Props {
  readonly classes: ClassNameMap<'button'>;
  readonly customerId: number;
  readonly priceListId: number;
  readonly kitId: number | null;
  readonly reportAlert: (message: string) => void;
  readonly onKitRemoved: () => void;
}

interface KitFormRemoval {
  id: number;
  name: string;
  checked: boolean;
  disabled: boolean;
  assignedKitNames: string[];
  activeOrders?: Order[];
}

type FormWithOccurrences = KitForm & { assignedKitNames: string[] };

const useStyles = makeStyles(theme => ({
  button: {
  },
  formRow: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  descriptionText: {
    marginBottom: theme.spacing(2)
  },
  buttonRow: {
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-start'
  },
  formName: {
    flex: '0 0 400px',
    paddingRight: theme.spacing(2),
    wordBreak: 'break-word'
  },
  accordions: {
    flex: '1 1 auto'
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    minWidth: 800
  }
}));

const RemoveKitWithFormsButton = (props: Props) => {
  const classes = useStyles();
  const kitApi = useKitApi();
  const formApi = useFormApi();
  const orderApi = useOrderApi();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [kitFormsForRemoval, setKitFormsForRemoval] = useState<KitFormRemoval[]|null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const loadKitForms = async () => {
    setKitFormsForRemoval(null);
    setIsLoading(true);
    try {
      const kitForms = await kitApi.fetchKitFormDefinitions(props.customerId, props.kitId!);
      const kits = await kitApi.fetchKits(props.customerId, props.priceListId);
      const otherKits = kits
        .filter((kit: any) => kit.id !== props.kitId);
      const formsAssignedToOtherKits = await getFormIdsAssignedToOtherKits(otherKits);
      const assignedNamesByFormId = new Map<number, string[]>(formsAssignedToOtherKits.map(f => [f.form.id, f.assignedKitNames]));

      const formsWithOrders = await Promise.all(kitForms.map(async (kitForm: KitForm) => {
        const formId = kitForm.form.id;
        const assignedKitNames = assignedNamesByFormId.get(formId) || [];
        let orders: Order[] = [];
        try {
          orders = await orderApi.fetchFormActiveOrders(formId);
        } catch (e) {
          orders = [];
        }
        const disabledBecauseAssigned = assignedKitNames.length > 0;
        const disabledBecauseOrders = orders.length > 0;
        const disabled = disabledBecauseAssigned || disabledBecauseOrders;
        const checked = !disabled;
        return {
          id: formId,
          name: (kitForm.form as NamedIdentifiable).name,
          checked,
          disabled,
          assignedKitNames,
          activeOrders: orders
        } as KitFormRemoval;
      }));
      setKitFormsForRemoval(formsWithOrders);
    } catch (reason) {
      props.reportAlert(`Nie można pobrać danych o formatkach: ${reason}`);
      setKitFormsForRemoval([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFormIdsAssignedToOtherKits = async (otherKits: any[]): Promise<FormWithOccurrences[]> => {
    const map = new Map<number, { kitForm: KitForm; assignedKitNames: Set<string> }>();
    await Promise.all(otherKits.map(async (otherKit) => {
      const otherKitForms = await kitApi.fetchKitFormDefinitions(props.customerId, otherKit.id);
      otherKitForms.forEach((otherKitForm: KitForm) => {
        const id = otherKitForm.form.id;
        const entry = map.get(id);
        const kitName = (otherKit && otherKit.name) ? otherKit.name : String(otherKit.id);
        if (entry) {
          entry.assignedKitNames.add(kitName);
        } else {
          const s = new Set<string>();
          s.add(kitName);
          map.set(id, { kitForm: otherKitForm, assignedKitNames: s });
        }
      });
    }));
    return Array.from(map.values()).map(v => ({ ...v.kitForm, assignedKitNames: Array.from(v.assignedKitNames) }));
  };

  const handleDialogOpen = () => {
    if (props.kitId == null) return;
    setDialogOpen(true);
    loadKitForms();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setKitFormsForRemoval(null);
  };

  const handleFormCheckboxChanged = (formId: number, checked: boolean) => {
    setKitFormsForRemoval(currentForms => currentForms?.map(form =>
      form.id === formId && !form.disabled
        ? { ...form, checked }
        : form
    ) || null);
  };

  const handleDeleteClicked = async () => {
    setIsDeleting(true);
    try {
      await kitApi.deleteKit(props.customerId, props.priceListId, props.kitId!);
      const formsToDelete = kitFormsForRemoval?.filter(form => form.checked) || [];
      for (const form of formsToDelete) {
        await formApi.deleteForm(props.customerId, props.priceListId, form.id);
      }
      handleDialogClose();
      props.onKitRemoved();
    } catch (reason) {
      props.reportAlert(`Nie można usunąć kompletu lub formatek: ${reason}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return <>
    <Dialog open={dialogOpen} maxWidth={false}>
      <Box className={classes.dialogContent}>
        <h2>Usuń komplet wraz z formatkami</h2>
        { isLoading || kitFormsForRemoval === null ? (
          <div>Ładowanie...</div>
        ) : kitFormsForRemoval.length === 0 ? (
          <Typography>Ten komplet nie zawiera formatek</Typography>
        ) : (
          <>
            <Typography className={classes.descriptionText}>
              Zaznaczone formatki zostaną usunięte razem z kompletem.<br/>
              Formatek przypisanych do innych kompletów oraz wchodzących w skład aktywych zamówień nie można usunąć.
            </Typography>
            { kitFormsForRemoval.map(form => (
              <div key={form.id} className={classes.formRow}>
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 400px', paddingRight: 16 }}>
                  <Checkbox
                    checked={ form.checked }
                    disabled={ form.disabled || isDeleting }
                    onChange={ (event, checked) => handleFormCheckboxChanged(form.id, checked) }
                    color='primary'
                  />
                  <div className={classes.formName}>{form.name}</div>
                </div>
                <div className={classes.accordions}>
                  {form.assignedKitNames && form.assignedKitNames.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>Przypisane do innych kompletów ({form.assignedKitNames.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {form.assignedKitNames.map(name => <li key={name}>{name}</li>)}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  {form.activeOrders && form.activeOrders.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>Aktywne zamówienia ({form.activeOrders.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {form.activeOrders.map(o => (
                            <li key={o.number || o.creationDate}>{o.number || o.creationDate}</li>
                          ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </Box>
      <Box className={classes.buttonRow}>
        <Button
          color='primary'
          className={ classes.button }
          onClick={ handleDeleteClicked }
          variant='contained'
          disabled={ isLoading || isDeleting }
          fullWidth
        >
          Usuń komplet i wybrane formatki
        </Button>
        <Button
          color='secondary'
          className={ classes.button }
          onClick={ handleDialogClose }
          variant='contained'
          fullWidth
        >
          Anuluj
        </Button>
      </Box>
    </Dialog>
    <Tooltip title='Usuń komplet wraz z formatkami'>
      <Fab
        className={props.classes.button}
        color='secondary'
        disabled={ isLoading || isDeleting || props.kitId == null }
        size='medium'
        onClick={ handleDialogOpen }
      >
        <DeleteSweepIcon />
      </Fab>
    </Tooltip>
  </>;
};

export default RemoveKitWithFormsButton;